import React, { useEffect, useState, ChangeEvent } from 'react';
import styled from 'styled-components';
import accountingService from '../../services/accountingService';
import { Account } from '../../types';

const ChartOfAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grouped'>('list');

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    filterAccounts();
  }, [accounts, searchTerm, filterType]);

  const loadAccounts = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await accountingService.getAccounts();
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const filterAccounts = (): void => {
    let filtered = accounts;

    if (searchTerm) {
      filtered = filtered.filter(
        (acc) =>
          acc.account_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          acc.account_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter((acc) => acc.account_type === filterType);
    }

    setFilteredAccounts(filtered);
  };

  const getAccountTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      asset: 'Assets',
      liability: 'Liabilities',
      equity: 'Equity',
      revenue: 'Revenue',
      expense: 'Expenses',
    };
    return labels[type] || type;
  };

  const getAccountTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      asset: '#0066CC',
      liability: '#D32F2F',
      equity: '#7B1FA2',
      revenue: '#2E7D32',
      expense: '#ED6C02',
    };
    return colors[type] || '#666';
  };

  const handleExport = (): void => {
    // Export accounts to CSV
    const headers = ['Code', 'Name', 'Type', 'Group', 'Normal Balance', 'Status'];
    const rows = filteredAccounts.map(acc => [
      acc.account_code,
      acc.account_name,
      acc.account_type,
      acc.account_group || '',
      acc.normal_balance,
      acc.is_active ? 'Active' : 'Inactive'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `chart-of-accounts-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = (): void => {
    // TODO: Implement import functionality with modal/file upload
    alert('Import feature coming soon. You will be able to upload CSV files to bulk import accounts.');
  };

  const handleNewAccount = (): void => {
    // TODO: Implement new account modal/form
    alert('New Account feature coming soon. You will be able to create new accounts with code, name, type, and other details.');
  };

  if (loading) {
    return (
      <Container>
        <LoadingOverlay>
          <LoadingSpinner />
          <LoadingText>Loading Chart of Accounts...</LoadingText>
        </LoadingOverlay>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <HeaderLeft>
          <PageTitle>Chart of Accounts</PageTitle>
          <AccountCount>{filteredAccounts.length} accounts</AccountCount>
        </HeaderLeft>
        <HeaderRight>
          <SecondaryButton onClick={handleImport}>
            <ButtonIcon>⚙</ButtonIcon>
            Import
          </SecondaryButton>
          <SecondaryButton onClick={handleExport}>
            <ButtonIcon>📥</ButtonIcon>
            Export
          </SecondaryButton>
          <PrimaryButton onClick={handleNewAccount}>
            <ButtonIcon>+</ButtonIcon>
            New Account
          </PrimaryButton>
        </HeaderRight>
      </PageHeader>

      <ContentCard>
        <Toolbar>
          <SearchContainer>
            <SearchIcon>🔍</SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search by account code, name, or description..."
              value={searchTerm}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearButton onClick={() => setSearchTerm('')}>✕</ClearButton>
            )}
          </SearchContainer>

          <ToolbarRight>
            <FilterGroup>
              <FilterChip
                $active={filterType === 'all'}
                onClick={() => setFilterType('all')}
              >
                All
              </FilterChip>
              <FilterChip
                $active={filterType === 'asset'}
                onClick={() => setFilterType('asset')}
                $color="#0066CC"
              >
                Assets
              </FilterChip>
              <FilterChip
                $active={filterType === 'liability'}
                onClick={() => setFilterType('liability')}
                $color="#D32F2F"
              >
                Liabilities
              </FilterChip>
              <FilterChip
                $active={filterType === 'equity'}
                onClick={() => setFilterType('equity')}
                $color="#7B1FA2"
              >
                Equity
              </FilterChip>
              <FilterChip
                $active={filterType === 'revenue'}
                onClick={() => setFilterType('revenue')}
                $color="#2E7D32"
              >
                Revenue
              </FilterChip>
              <FilterChip
                $active={filterType === 'expense'}
                onClick={() => setFilterType('expense')}
                $color="#ED6C02"
              >
                Expenses
              </FilterChip>
            </FilterGroup>

            <ViewToggle>
              <ViewButton
                $active={viewMode === 'list'}
                onClick={() => setViewMode('list')}
              >
                ☰
              </ViewButton>
              <ViewButton
                $active={viewMode === 'grouped'}
                onClick={() => setViewMode('grouped')}
              >
                ⊞
              </ViewButton>
            </ViewToggle>
          </ToolbarRight>
        </Toolbar>

        {filteredAccounts.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📊</EmptyIcon>
            <EmptyTitle>No accounts found</EmptyTitle>
            <EmptyText>
              {accounts.length === 0
                ? 'Get started by creating your first account'
                : 'Try adjusting your search or filter criteria'}
            </EmptyText>
            {accounts.length === 0 && (
              <PrimaryButton>
                <ButtonIcon>+</ButtonIcon>
                Create Account
              </PrimaryButton>
            )}
          </EmptyState>
        ) : (
          <TableContainer>
            <Table>
              <thead>
                <TableRow $header>
                  <TableHeader $width="10%">Code</TableHeader>
                  <TableHeader $width="30%">Account Name</TableHeader>
                  <TableHeader $width="15%">Type</TableHeader>
                  <TableHeader $width="15%">Group</TableHeader>
                  <TableHeader $width="10%">Balance</TableHeader>
                  <TableHeader $width="10%">Status</TableHeader>
                  <TableHeader $width="10%" $align="center">Actions</TableHeader>
                </TableRow>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => (
                  <TableRow key={account.account_id} $hover>
                    <TableCell>
                      <AccountCode>{account.account_code}</AccountCode>
                    </TableCell>
                    <TableCell>
                      <AccountName>{account.account_name}</AccountName>
                      {account.description && (
                        <AccountDesc>{account.description}</AccountDesc>
                      )}
                    </TableCell>
                    <TableCell>
                      <TypeBadge $color={getAccountTypeColor(account.account_type)}>
                        {getAccountTypeLabel(account.account_type)}
                      </TypeBadge>
                    </TableCell>
                    <TableCell>
                      <GroupText>{account.account_group}</GroupText>
                    </TableCell>
                    <TableCell>
                      <BalanceText $type={account.normal_balance}>
                        {account.currency_code || 'LKR'} 0.00
                      </BalanceText>
                    </TableCell>
                    <TableCell>
                      <StatusBadge $active={account.is_active}>
                        <StatusDot $active={account.is_active} />
                        {account.is_active ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell $align="center">
                      <ActionMenu>
                        <ActionButton title="View Details">👁</ActionButton>
                        <ActionButton title="Edit Account">✏</ActionButton>
                        <ActionButton title="View Ledger">📖</ActionButton>
                        <ActionButton title="More Options">⋮</ActionButton>
                      </ActionMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}
      </ContentCard>

      <Footer>
        <FooterText>
          Showing {filteredAccounts.length} of {accounts.length} accounts
        </FooterText>
        <FooterActions>
          <FooterLink>Account Settings</FooterLink>
          <FooterSeparator>•</FooterSeparator>
          <FooterLink>Help</FooterLink>
        </FooterActions>
      </Footer>
    </Container>
  );
};

// ==================== STYLED COMPONENTS ====================

const Container = styled.div`
  min-height: calc(100vh - 60px);
  background: #F8F9FA;
  padding: 24px 32px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #1A1A1A;
  margin: 0;
  letter-spacing: -0.5px;
`;

const AccountCount = styled.span`
  font-size: 14px;
  color: #666666;
  font-weight: 400;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #0066CC;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background: #0052A3;
    box-shadow: 0 4px 6px rgba(0, 102, 204, 0.2);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: white;
  color: #1A1A1A;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #0066CC;
    color: #0066CC;
    background: #F8FAFB;
  }
`;

const ButtonIcon = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Toolbar = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  opacity: 0.5;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px 42px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 14px;
  color: #1A1A1A;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #0066CC;
    box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.1);
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9CA3AF;
  cursor: pointer;
  font-size: 18px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #666;
  }
`;

const ToolbarRight = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FilterChip = styled.button<{ $active: boolean; $color?: string }>`
  padding: 6px 14px;
  border: 1px solid ${props => props.$active ? (props.$color || '#0066CC') : '#E5E7EB'};
  border-radius: 20px;
  background: ${props => props.$active ? (props.$color || '#0066CC') : 'white'};
  color: ${props => props.$active ? 'white' : '#666'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    border-color: ${props => props.$color || '#0066CC'};
    background: ${props => props.$active ? (props.$color || '#0066CC') : '#F8F9FA'};
  }
`;

const ViewToggle = styled.div`
  display: flex;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  overflow: hidden;
`;

const ViewButton = styled.button<{ $active: boolean }>`
  padding: 8px 12px;
  background: ${props => props.$active ? '#F3F4F6' : 'white'};
  color: ${props => props.$active ? '#1A1A1A' : '#666'};
  border: none;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;

  &:not(:last-child) {
    border-right: 1px solid #E5E7EB;
  }

  &:hover {
    background: #F3F4F6;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  max-height: calc(100vh - 340px);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableRow = styled.tr<{ $header?: boolean; $hover?: boolean }>`
  ${props => props.$header && `
    background: #F9FAFB;
    position: sticky;
    top: 0;
    z-index: 10;
  `}

  ${props => props.$hover && `
    transition: background 0.15s;
    
    &:hover {
      background: #F8F9FA;
    }
  `}

  &:not(:last-child) {
    border-bottom: 1px solid #F3F4F6;
  }
`;

const TableHeader = styled.th<{ $width?: string; $align?: string }>`
  text-align: ${props => props.$align || 'left'};
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  ${props => props.$width && `width: ${props.$width};`}
`;

const TableCell = styled.td<{ $align?: string }>`
  padding: 16px;
  font-size: 14px;
  color: #1A1A1A;
  vertical-align: top;
  ${props => props.$align && `text-align: ${props.$align};`}
`;

const AccountCode = styled.span`
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  font-size: 13px;
  font-weight: 600;
  color: #0066CC;
`;

const AccountName = styled.div`
  font-weight: 500;
  color: #1A1A1A;
  margin-bottom: 4px;
`;

const AccountDesc = styled.div`
  font-size: 12px;
  color: #6B7280;
  line-height: 1.4;
`;

const TypeBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const GroupText = styled.span`
  color: #6B7280;
  font-size: 13px;
`;

const BalanceText = styled.span<{ $type: string }>`
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$type === 'debit' ? '#2E7D32' : '#1A1A1A'};
`;

const StatusBadge = styled.div<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: ${props => props.$active ? '#E8F5E9' : '#FFF3E0'};
  color: ${props => props.$active ? '#2E7D32' : '#ED6C02'};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const StatusDot = styled.span<{ $active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.$active ? '#4CAF50' : '#FF9800'};
`;

const ActionMenu = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #F3F4F6;
    border-color: #E5E7EB;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1A1A1A;
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6B7280;
  margin: 0 0 24px 0;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 0 8px;
`;

const FooterText = styled.span`
  font-size: 13px;
  color: #6B7280;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const FooterLink = styled.a`
  font-size: 13px;
  color: #0066CC;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const FooterSeparator = styled.span`
  color: #D1D5DB;
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #E5E7EB;
  border-top-color: #0066CC;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  margin-top: 16px;
  font-size: 14px;
  color: #6B7280;
`;

export default ChartOfAccounts;
